//
//  BloodPressureScatterChart.tsx
//
//  isana.net,inc. Proprietary and Confidential
//

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceArea,
  LabelList,
  ZAxis,
} from "recharts";

export interface BloodPressureData {
  x: number;
  y: number;
  dateString: string;
}

interface BloodPressureScatterChartProps {
  data: BloodPressureData[];
}

const BASIC_DOMAINS = Object.freeze({
  defaultMinX: 50, // 拡張期血圧（最小血圧）[最小値、最大値]
  defaultMaxX: 110,
  defaultMinY: 70, // 収縮期血圧（最大血圧）[最小値、最大値]
  defaultMaxY: 160,
  defaultDiff: 10, // デフォルトの差分
});

const COLORS = {
  bloodPressure: "skyblue",
};
const BloodPressureScatterChart: React.FC<BloodPressureScatterChartProps> = ({
  data,
}) => {
  const [todayData, setTodayData] = useState<any[]>([]);

  const domain = useMemo(() => {
    const newDomain = {
      x_domain: [BASIC_DOMAINS.defaultMinX, BASIC_DOMAINS.defaultMaxX],
      y_domain: [BASIC_DOMAINS.defaultMinY, BASIC_DOMAINS.defaultMaxY],
    };

    if (todayData.length > 0) {
      const { x, y } = todayData[0];
      // グラフの表示を見やすくするため、
      // 測定値に±10の余裕があるようにdmainの値を調整する
      const domain = {
        x_domain: [
          Math.min(BASIC_DOMAINS.defaultMinX, x - BASIC_DOMAINS.defaultDiff),
          Math.max(BASIC_DOMAINS.defaultMaxX, x + BASIC_DOMAINS.defaultDiff),
        ],
        y_domain: [
          Math.min(BASIC_DOMAINS.defaultMinY, y - BASIC_DOMAINS.defaultDiff),
          Math.max(BASIC_DOMAINS.defaultMaxY, y + BASIC_DOMAINS.defaultDiff),
        ],
      };
      return domain;
    }

    return newDomain;
  }, [todayData]);

  const referenceData = useMemo(() => {
    return [
      {
        x1: domain.x_domain[0],
        x2: 75,
        y1: domain.y_domain[0],
        y2: 115,
        label: "正常血圧",
        color: "rgb(208, 231, 213)",
      },
      {
        x1: 75,
        x2: 75,
        y1: 115,
        y2: 125,
        label: "正常高値血圧",
        color: "rgb(254, 250, 199)",
      },
      {
        x1: 75,
        x2: 85,
        y1: 125,
        y2: 135,
        label: "高値血圧",
        color: "rgb(245, 193, 135)",
      },
      {
        x1: 85,
        x2: domain.x_domain[1],
        y1: 135,
        y2: domain.y_domain[1],
        label: "高血圧",
        color: "rgb(240, 151, 163)",
      },
    ];
  }, [domain]);

  const getTodayData = useCallback(() => {
    if (data.length === 0) {
      return [];
    } else {
      return [
        {
          label: "今日の測定値",
          x: data[0].x,
          y: data[0].y,
        },
      ];
    }
  }, []);
  useEffect(() => {
    setTodayData(getTodayData());
  }, [data]);
  return (
    <ResponsiveContainer width="80%" height="100%" key={Math.random()}>
      <ScatterChart margin={{ top: 30, right: 40, bottom: 30, left: 40 }}>
        <XAxis
          type="number"
          dataKey="x"
          name="拡張期血圧（最小血圧）"
          domain={domain.x_domain}
          ticks={[75, 85]}
        />
        <YAxis
          type="number"
          dataKey="y"
          name="収縮期血圧（最大血圧）"
          domain={domain.y_domain}
          ticks={[115, 125, 135]}
        />
        <ZAxis range={[150]} />
        {referenceData.map((data, index) =>
          index === 0 ? (
            <ReferenceArea
              key={index}
              x1={data.x1}
              x2={data.x2}
              y1={data.y1}
              y2={data.y2}
              fill={data.color}
              label={data.label}
              opacity={1}
            />
          ) : (
            <ReferenceArea
              key={`${index}:0`}
              x1={domain.x_domain[0]}
              x2={data.x2}
              y1={data.y1}
              y2={data.y2}
              fill={data.color}
              label={data.label}
              fillOpacity={1}
              opacity={1}
            />
          )
        )}{" "}
        {referenceData.map(
          (data, index) =>
            index !== 0 && (
              <ReferenceArea
                key={`${index}:1`}
                x1={data.x1}
                x2={data.x2}
                y1={domain.y_domain[0]}
                y2={data.y1}
                fillOpacity={1}
                fill={data.color}
              />
            )
        )}
        <Scatter data={todayData} fill={COLORS.bloodPressure} shape="circle">
          <LabelList dataKey="label" position="top" />
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
};

export default BloodPressureScatterChart;
